import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Table } from '@tanstack/react-table';
import { SourcingOpenings, SourcingService } from 'api-clients/monolith';
import _isEmpty from 'lodash/isEmpty';
import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import TrashIcon from '@/assets/icons/trash.svg';
import { useToaster } from '@/components/base/toaster';
import { PlaneSendIcon } from '@/components/icons/plane-send';
import { ActionToast } from '@/components/ui/action-toast';
import { useAuthContext } from '@/contexts/auth-context';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useTableContext } from '@/contexts/table-context';
import { useIsFirstJobPostOnAccount } from '@/hooks/use-is-first-job-post-on-account';
import { usePostHog } from '@/hooks/use-posthog';
import { queryKeys } from '@/query-keys';

interface RowsSelectProps {
  table: Table<SourcingOpenings>;
  refetchOpenings: () => Promise<void>;
}

export function RowsSelect({ table, refetchOpenings }: RowsSelectProps) {
  const queryClient = useQueryClient();
  const { setRowSelection } = useTableContext();
  const { rowSelection } = table.getState();
  const { notifySuccess, notifyError } = useToaster();
  const intl = useIntl();
  const { canPostToJobBoards } = useAuthContext();
  const { openJobPostingForm } = useJobPostingContext();
  const posthog = usePostHog();
  const isFirstJobPostOnAccount = useIsFirstJobPostOnAccount();

  const selectedIds = useMemo(
    () => (_isEmpty(rowSelection) ? [] : Object.keys(rowSelection)),
    [rowSelection],
  );

  // The table only ever holds one page of rows (server-side pagination), but
  // selection persists across pages. Cache the full opening data for each
  // selected row as its page is visited, so openings selected on a page the
  // user has since navigated away from aren't silently dropped when building
  // `selectedOpenings` below (e.g. before opening the job posting form).
  const selectedOpeningsCacheRef = useRef<Map<string, SourcingOpenings>>(
    new Map(),
  );

  // Included as a memo dependency below (instead of just `table`) so a
  // refetch that replaces this page's row data without changing
  // `rowSelection` refreshes the cache, rather than retaining stale
  // pre-refetch values (e.g. `active`).
  const filteredRows = table.getFilteredRowModel().rows;

  const selectedOpenings = useMemo(() => {
    const selectedIdSet = new Set(selectedIds);
    const cache = selectedOpeningsCacheRef.current;

    filteredRows.forEach(row => {
      if (selectedIdSet.has(row.id)) cache.set(row.id, row.original);
    });

    Array.from(cache.keys()).forEach(id => {
      if (!selectedIdSet.has(id)) cache.delete(id);
    });

    return selectedIds
      .map(id => cache.get(id))
      .filter((opening): opening is SourcingOpenings => opening != null);
  }, [selectedIds, filteredRows]);

  const { mutate: updateSelection } = useMutation({
    mutationFn: () =>
      SourcingService.postInternalApiSourcingOpeningsMarkForSourcing({
        unselected_opening_ids: selectedIds,
      }),
    onSuccess: async () => {
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Selected openings updated successfully!',
          description:
            'Success toast after bulk add/remove on sourcing dashboard',
        }),
      });
      setRowSelection({});
      await refetchOpenings();
      await queryClient.invalidateQueries({
        queryKey: [...queryKeys.dashboard.recommendations.sourcing],
      });
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update the selected openings!',
          description: 'Error toast when bulk dashboard selection update fails',
        }),
      });
    },
  });

  const onSelectedOpeningsSubmit = useCallback(() => {
    updateSelection();
  }, [updateSelection]);

  const handlePostJobClick = useCallback(() => {
    posthog.capture('source_post_job:button_click', {
      entry_point: 'sponsored_postings',
      is_first_job_post_on_account: isFirstJobPostOnAccount,
    });
    openJobPostingForm({
      openings: selectedOpenings.map(opening => ({
        id: opening.external_id,
        title: opening.title,
        active: opening.active,
        isSourcingEnabled: opening.is_sourcing_funnel,
      })),
      entryPoint: 'sponsored_postings',
    });
  }, [isFirstJobPostOnAccount, openJobPostingForm, posthog, selectedOpenings]);

  const actionButtons = useMemo(() => {
    const buttons = [];

    // Only show post job button if user has permission
    if (canPostToJobBoards) {
      buttons.push({
        label: intl.formatMessage({
          defaultMessage: 'Post job',
          description:
            'Bulk action: open post-to-channels for selected openings',
        }),
        onClick: handlePostJobClick,
        icon: <PlaneSendIcon className="text-fx-neutral-400" />,
      });
    }

    buttons.push({
      label: intl.formatMessage({
        defaultMessage: 'Remove selection from dashboard',
        description:
          'Bulk action: unpin selected roles from the sourcing dashboard',
      }),
      onClick: onSelectedOpeningsSubmit,
      icon: <img src={TrashIcon} alt="Chart bar" />,
    });

    return buttons;
  }, [canPostToJobBoards, handlePostJobClick, intl, onSelectedOpeningsSubmit]);

  if (_isEmpty(selectedIds)) return <></>;

  return (
    <ActionToast
      selectedCount={selectedIds.length}
      onDismiss={() => setRowSelection({})}
      actionButtons={actionButtons}
    />
  );
}
